/**
 * RecodeWizard — modal that builds a `RECODE` statement visually.
 *
 * Flow:
 *   1. On open, scan the questionnaire for recode targets (visible Q + HIDDEN)
 *      and their answer options.
 *   2. If `initialRecodeText` is set, parse it into a RecodeForm for editing.
 *   3. Pick a target → the wizard offers the modes valid for its type
 *      (bucket-by-condition / SELECT_N / arithmetic).
 *   4. On Apply, call `onApply(generateRecode(form))`.
 */
import React, { useMemo, useState } from "react"
import { Alert, ConfigProvider, Modal, Segmented, Tooltip } from "antd"
import { RetweetOutlined } from "@ant-design/icons"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { LabelItem, LabelType, parseLabelsAndLists } from "utils/labelUtils"
import { htmlToText } from "utils/htmlTextConverter"
import { QuestionInfo } from "utils/logicWizard/questionTypeDetector"
import { collectRecodeTargets } from "utils/recodeWizard/recodeTargets"
import {
  allowedModesFor,
  isMultiSelect,
  isRecodeComplete,
  makeEmptyRecodeForm,
  RecodeForm,
  RecodeMode,
} from "utils/recodeWizard/recodeModel"
import { generateRecode } from "utils/recodeWizard/recodeGenerator"
import { parseRecodeBlock } from "utils/recodeWizard/recodeParser"
import QuestionPicker from "../LogicWizard/QuestionPicker.component"
import BucketByConditionEditor from "./BucketByConditionEditor.component"
import SelectNEditor from "./SelectNEditor.component"
import ArithmeticEditor from "./ArithmeticEditor.component"

interface RecodeWizardProps {
  isOpen: boolean
  onClose: () => void
  /** Called with the full RECODE block when the user clicks Apply. */
  onApply: (text: string) => void
  /** Full questionnaire HTML (or plain text); the wizard derives labels from it. */
  questionnaireHtml: string
  /** If set, parsed to populate the form for editing. */
  initialRecodeText?: string | null
}

const MODE_LABELS: Record<RecodeMode, string> = {
  bucket: "Set answers by condition",
  select_n: "Top-N / random",
  arithmetic: "Calculate (number)",
}

function computeInitialForm(text: string | null | undefined): {
  form: RecodeForm
  errors: string[]
} {
  if (!text || !text.trim()) {
    return { form: makeEmptyRecodeForm(), errors: [] }
  }
  return parseRecodeBlock(text)
}

const RecodeWizard: React.FC<RecodeWizardProps> = ({
  isOpen,
  onClose,
  onApply,
  questionnaireHtml,
  initialRecodeText,
}) => {
  const plainText = useMemo(
    () => htmlToText(questionnaireHtml),
    [questionnaireHtml],
  )
  const baseLabels = useMemo<LabelItem[]>(
    () => parseLabelsAndLists(plainText),
    [plainText],
  )
  const targets = useMemo(
    () => collectRecodeTargets(plainText, baseLabels),
    [plainText, baseLabels],
  )
  const listMap = useMemo<ReadonlyMap<string, LabelItem>>(() => {
    const m = new Map<string, LabelItem>()
    for (const l of baseLabels) {
      if (l.type === LabelType.LIST || l.type === LabelType.RANGE_LIST) {
        m.set(l.name, l)
      }
    }
    return m
  }, [baseLabels])
  const multiSelectSources = useMemo<LabelItem[]>(
    () => targets.labels.filter((l) => isMultiSelect(targets.info.get(l.name))),
    [targets],
  )

  const [form, setForm] = useState<RecodeForm>(
    () => computeInitialForm(initialRecodeText).form,
  )
  const [parseErrors] = useState<string[]>(
    () => computeInitialForm(initialRecodeText).errors,
  )

  const targetInfo: QuestionInfo | undefined = targets.info.get(form.target)
  const allowedModes = allowedModesFor(form.target ? targetInfo : undefined)

  const preview = useMemo(() => {
    try {
      return generateRecode(form)
    } catch {
      return "RECODE …"
    }
  }, [form])

  const canApply = useMemo(() => isRecodeComplete(form), [form])

  const handleTargetChange = (target: string) => {
    const modes = allowedModesFor(targets.info.get(target))
    setForm((f) => ({
      ...f,
      target,
      mode: modes.includes(f.mode) ? f.mode : (modes[0] ?? "bucket"),
    }))
  }

  const handleApply = () => {
    if (!canApply) return
    onApply(generateRecode(form))
    onClose()
  }

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: "#36C3ED",
          colorLink: "#36C3ED",
          colorLinkHover: "#2BA8CF",
          colorLinkActive: "#1F8AAB",
        },
      }}
    >
      <Modal
        open={isOpen}
        onCancel={onClose}
        width={1100}
        destroyOnHidden
        title={
          <span>
            <RetweetOutlined style={{ marginRight: 8 }} />
            Recode wizard
          </span>
        }
        footer={
          <div className="flex justify-between items-center w-full">
            <span className="text-xs text-gray-500">
              {canApply
                ? "Ready to apply"
                : "Pick a target and complete the recode to enable Apply"}
            </span>
            <div className="flex gap-2">
              <DataQButton type="secondary" onClick={onClose}>
                Cancel
              </DataQButton>
              <Tooltip
                title={
                  canApply ? "" : "Finish the target and every value first."
                }
              >
                <DataQButton
                  type="primary"
                  onClick={handleApply}
                  isDisabled={!canApply}
                >
                  Apply
                </DataQButton>
              </Tooltip>
            </div>
          </div>
        }
      >
        {parseErrors.length > 0 && (
          <Alert
            showIcon
            type="warning"
            className="mb-3"
            message="We couldn't fully parse this RECODE"
            description={
              <ul className="list-disc pl-5 text-xs mb-0">
                {parseErrors.slice(0, 5).map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
                {parseErrors.length > 5 && (
                  <li>…and {parseErrors.length - 5} more</li>
                )}
              </ul>
            }
          />
        )}

        <div className="text-sm font-medium mb-2">Recode this question…</div>
        <QuestionPicker
          labels={targets.labels}
          questionInfo={targets.info}
          value={form.target}
          onChange={handleTargetChange}
          placeholder="Select the question to recode into…"
        />

        {form.target && allowedModes.length === 0 && (
          <Alert
            className="mt-3"
            type="info"
            showIcon
            message="This question type can't be recoded"
            description="RECODE supports single/multi-select and fill-in-number targets."
          />
        )}

        {form.target && allowedModes.length > 0 && (
          <div className="mt-4 flex flex-col gap-3">
            {allowedModes.length > 1 && (
              <Segmented
                value={form.mode}
                onChange={(v) => setForm((f) => ({ ...f, mode: v as RecodeMode }))}
                options={allowedModes.map((m) => ({
                  label: MODE_LABELS[m],
                  value: m,
                }))}
              />
            )}

            {form.mode === "bucket" && (
              <BucketByConditionEditor
                targetInfo={targetInfo}
                buckets={form.buckets}
                labels={targets.labels}
                questionInfo={targets.info}
                listMap={listMap}
                onChange={(buckets) => setForm((f) => ({ ...f, buckets }))}
              />
            )}

            {form.mode === "select_n" && (
              <SelectNEditor
                value={form.selectN}
                sourceLabels={multiSelectSources}
                questionInfo={targets.info}
                listMap={listMap}
                onChange={(selectN) => setForm((f) => ({ ...f, selectN }))}
              />
            )}

            {form.mode === "arithmetic" && (
              <ArithmeticEditor
                value={form.arithmeticText}
                refLabels={targets.labels}
                onChange={(arithmeticText) =>
                  setForm((f) => ({ ...f, arithmeticText }))
                }
              />
            )}
          </div>
        )}

        <div className="mt-4 p-3 bg-gray-900 text-gray-100 rounded font-mono text-sm whitespace-pre-wrap break-words">
          {preview}
        </div>
        <div className="mt-1 text-xs text-gray-500">
          This is exactly what gets written to the editor.
        </div>
      </Modal>
    </ConfigProvider>
  )
}

export default RecodeWizard
